"use client";

import { useSearchParams } from "next/navigation";
import { ShoppingBag, User2, MapPin, Truck, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import SideCartV2 from "@/components/common/SideCartV2";
import SelectField from "../common/SelectBox";
import { fetchAllCurrencies } from "@/api-service/currency";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { Currency } from "@/packages/types/currency";
import { useAuth } from "@/packages/context/AuthContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { useCart } from "@/packages/context/CartContext";
import LABELS from "@/components/global-constant/src/general";
export default function HeaderActions() {
  const { cartItems, isCartOpen, setIsCartOpen } = useCart();
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string; symbol: string; exchangeRate: number; id: number }[]
  >([]);

  const { code: currencyCode, changeCurrency, isInitialized } = useCurrency();
  const { isLoggedIn, logout, user } = useAuth();
  const { fastPush } = useFastRouter();
  // navigate to store details page instead of opening inline drawer
  // const { wishlistItems } = useWishlist();
  const searchParams = useSearchParams();

  // Load currencies and check URL param
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const res = await fetchAllCurrencies();
        const options = res.data.map((c: Currency) => ({
          label: c.symbol,
          value: c.code,
          symbol: c.symbol,
          exchangeRate: c.exchangeRate,
          id: c.id ?? 0,
        }));
        setCurrencyOptions(options);

        const currencyFromUrl = searchParams.get("currency");
        if (currencyFromUrl) {
          const matched = res.data.find(
            (c: Currency) => c.code?.toUpperCase() === currencyFromUrl.toUpperCase()
          );
          if (matched && matched.code?.toUpperCase() !== currencyCode?.toUpperCase()) {
            changeCurrency({
              id: matched.id,
              symbol: matched.symbol,
              code: matched.code,
              exchangeRate: matched.exchangeRate,
            });
          }
        }
      } catch (err) {
        console.error("Failed to fetch currencies", err);
      }
    };
    loadCurrencies();
  }, [searchParams, changeCurrency, currencyCode]);


  // Handle currency change
  const handleCurrencyChange = (val: string) => {
    const selected = currencyOptions.find((c) => c.value == val);
    if (selected) {
      changeCurrency({
        id: selected.id,
        symbol: selected.symbol,
        code: selected.value,
        exchangeRate: selected.exchangeRate,
      });

      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", val);
      fastPush(`?${params.toString()}`);
    }
  };

  // Icon Button Component
  const IconButton = ({
    icon: Icon,
    badge,
    onClick,
    title,
    gradient = false,
  }: {
    icon: React.ReactNode;
    badge?: number;
    onClick: () => void;
    title: string;
    gradient?: boolean;
  }) => (
    <button
      onClick={onClick}
      title={title}
      className={`group relative flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-300 ${
        gradient
          ? "bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-lg hover:shadow-xl hover:from-slate-800 hover:to-slate-700"
          : "border border-slate-200/50 bg-white/40 text-slate-700 backdrop-blur-sm hover:border-slate-300 hover:bg-slate-50/60 hover:shadow-md"
      } active:scale-95`}
    >
      {/* Badge */}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-600 text-xs font-bold text-white shadow-lg ring-2 ring-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}

      {/* Icon with animation */}
      <div className="transition-transform duration-300 group-hover:scale-110">
        {Icon}
      </div>
    </button>
  );

  return (
    <>
      <div className="flex items-center gap-3 ">
        {/* Currency Selector (Hidden UI, URL & Context logic retained) */}
        <div className="w-[75px] capitalize hidden">
          {!isInitialized || currencyOptions.length === 0 ? (
            <div className="h-11 w-full animate-pulse rounded-xl border border-slate-200/50 bg-slate-100/50" />
          ) : (
            <SelectField
              name="currency"
              value={currencyCode}
              onChange={handleCurrencyChange}
              options={currencyOptions.map((c) => ({
                label: c.value,
                value: c.value,
              }))}
            />
          )}
        </div>

        {/* Cart Icon - Gradient */}
        <IconButton
          icon={<ShoppingBag className="h-5 w-5 cursor-pointer" />}
          badge={cartItems.reduce((acc, item) => acc + item.quantity, 0)}
          onClick={() => setIsCartOpen(true)}
          title={LABELS.HEADER.CART_TOOLTIP}
          gradient
        />

        {/* Store Details Icon navigates to dedicated page */}
        <IconButton
          icon={<MapPin className="h-5 w-5 cursor-pointer" />}
          onClick={() => fastPush("/store-details")}
          title={LABELS.HEADER.STORE_DETAILS_TOOLTIP}
        />

        {/* Shipping Policy Icon */}
        <IconButton
          icon={<Truck className="h-5 w-5 cursor-pointer" />}
          onClick={() => fastPush("/shipping-policy")}
          title="Shipping Policy"
        />

        {/* Profile Icon with Hover Dropdown */}
        {isLoggedIn ? (
          <div className="relative group">
            <IconButton
              icon={<User2 className="h-5 w-5 cursor-pointer" />}
              onClick={() => fastPush("/user-profile")}
              title={LABELS.HEADER.PROFILE_TOOLTIP}
            />

            {/* Hover Dropdown Menu */}
            <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] min-w-[160px]">
              <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 text-slate-700 overflow-hidden">
                {user?.email && (
                  <div className="px-3.5 py-1.5 border-b border-slate-100 font-medium text-[11px] text-slate-400 truncate">
                    {user.email}
                  </div>
                )}
                <button
                  onClick={() => fastPush("/user-profile")}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-semibold hover:bg-slate-50 hover:text-[var(--brand-accent)] transition-colors text-left cursor-pointer"
                >
                  <User2 className="w-4 h-4 text-slate-500" />
                  <span>Orders</span>
                </button>
                <button
                  onClick={() => logout()}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 transition-colors text-left border-t border-slate-100 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 text-rose-500" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <IconButton
            icon={<User2 className="h-5 w-5 cursor-pointer" />}
            onClick={() => fastPush("/sign-in")}
            title={LABELS.HEADER.SIGN_IN_TOOLTIP}
          />
        )}
      </div>

      {/* Side Cart Slide In */}
      {isCartOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 "
          onClick={() => setIsCartOpen(false)}
        >
          <div
            className={`fixed top-0 right-0 h-full w-[400px] bg-white shadow-lg z-50 transform transition-transform duration-300 ${
              isCartOpen ? "translate-x-0" : "translate-x-full"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <SideCartV2 onClose={() => setIsCartOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
